import { titleCase } from '../titleCase.ts';
import { BUILDER_START, DURATIONS, isAside, parseNeed, type BuilderNode, type BuilderTree, type BuilderWho } from './builder.ts';
import { PERMIT_KEY } from './gates.ts';
import { PHASE_KEYS, type PhaseKey } from './phases.ts';
import { SPECIAL_KEYS, SPECIAL_TASKS, specialsOf } from './specials.ts';
import { NODE_SOURCES, ProcessTreeDocSchema, TASK_OWNERS, chooseOf, type TaskOwner, type NodeKind, type ProcessNodeDefT, type ProcessTreeDoc, type TreePhase } from './schema.ts';

/**
 * Locking the process tree in (tech-tree spec v3 §8; Jason, 2026-10-06: "this
 * initial version ... is the starting point moving forward"): the tree as it is
 * built on the process tree page becomes the master tree jobs are made from,
 * as a new version, by rule.
 *
 * - Every step on the board, and the tasks in its list, keep their keys, so
 *   what the old tree knew of a step goes on with it: what the client reads,
 *   its ballpark days, its notes.
 * - A step's kind, who does it, its trade, phase and what it is are the
 *   process tree's. Its tasks come on with it.
 * - Depends-on becomes "comes after, where the job has it" (`before`, the only
 *   link): a job is the steps it has (`byHand`). A sign-off is a gate placed in
 *   the tree. A step held by the permit comes after Permits in hand.
 * - What is in the bin, or still only in the list, stays out.
 * - The phases come with it: their names, what closes each, how sure the
 *   price is once it is and their shares of the time, which the funnel reads
 *   (option A, 2026-10-06; shares, 2026-10-07); a board with none keeps the old
 *   tree's. One gate opens each phase after the first, the gate closing the
 *   phase before it: a tree where one has none is refused, in words.
 * - What the client reads of a step is its own note (`clientNote`), never NSB's.
 * - A decision keeps its options, their ids and words, and whether one of them is chosen or any (design study 22).
 * - One step carries each special task (Pattern mapping, the job's one language): a tree where two do is refused, in
 *   words.
 * - A step's holds are in its own list (Jason, 2026-10-06: "make them holds"):
 *   needing an inspection, its last task is the Inspection, so it is not done
 *   until the inspector passes it; needing a pre-install inspection, its first
 *   is NSB's check that the site is ready, and its work waits on that. A step
 *   with no tasks of its own gets one, The work, for them to hold.
 */

export interface LockedIn {
  doc: ProcessTreeDoc;
  /** What the old tree had that found no place, in words: to say, not to lose quietly. */
  left: string[];
}

/** Whose a step is on a job, from who the tree (or a job's own step's card) says does, decides or approves it: one rule for both. */
export const OWNER_OF_WHO: Record<BuilderWho, TaskOwner> = { nsb: 'nsb', sub: 'sub', supplier: 'vendor', client: 'client', outside: 'authority' };

const KIND: Record<BuilderNode['kind'], NodeKind> = { task: 'work', decision: 'decision', gate: 'gate', milestone: 'milestone', start: 'milestone', end: 'milestone' };
/** A kind in words, as a line of what lock-in leaves off says it. */
const KIND_WORDS_OF: Record<NodeKind, string> = { work: 'work', milestone: 'a milestone', decision: 'a decision', inspection: 'an inspection', gate: 'a gate' };

/** A step's time on the scale as ballpark days: the middle of its range, at least a day for anything that takes time. */
function daysOf(n: BuilderNode): number | undefined {
  if (!n.time) return undefined;
  const at = (k: string) => DURATIONS.find((d) => d.key === k)!.days;
  return Math.max(1, Math.round((at(n.time.low) + at(n.time.high)) / 2));
}

export function lockIn(t: BuilderTree, old: ProcessTreeDoc, from: number): LockedIn {
  const left: string[] = [];
  const was = new Map(old.nodes.map((n) => [n.key, n]));
  const byKey = new Map(t.nodes.map((n) => [n.key, n]));
  // On the board, out of the bin; a task while its step is.
  const live = (n: BuilderNode | undefined): boolean => {
    if (!n || isAside(n)) return false;
    if (n.parent === null) return n.at !== null;
    return live(byKey.get(n.parent));
  };
  const kept = t.nodes.filter((n) => live(n));
  const keep = new Set(kept.map((n) => n.key));
  const phaseRank = (n: BuilderNode) => {
    const top = n.parent === null ? n : byKey.get(n.parent)!;
    const i = PHASE_KEYS.indexOf(top.phase as PhaseKey);
    return i < 0 ? PHASE_KEYS.length : i;
  };
  // The tree's order: phase by phase, top to bottom as the board has it; each step's tasks just after it, in its list's order.
  const tops = kept
    .filter((n) => n.parent === null)
    .sort((a, b) => phaseRank(a) - phaseRank(b) || a.at!.y - b.at!.y || a.at!.x - b.at!.x || a.key.localeCompare(b.key));
  const ordered: BuilderNode[] = [];
  const withTasks = (n: BuilderNode) => {
    ordered.push(n);
    for (const c of kept.filter((x) => x.parent === n.key)) withTasks(c);
  };
  for (const n of tops) withTasks(n);

  // Depends-on, turned round: each step names what comes after it (`before`), among what is kept.
  const after = new Map<string, Set<string>>();
  const follow = (needed: string, by: string) => after.set(needed, new Set([...(after.get(needed) ?? []), by]));
  let binned = 0;
  for (const n of kept) {
    for (const d of n.dependsOn) {
      const k = parseNeed(d).key;
      if (k === n.key) continue;
      if (keep.has(k)) follow(k, n.key);
      else binned += 1;
    }
    if (n.permit && keep.has(PERMIT_KEY) && n.key !== PERMIT_KEY && !n.dependsOn.some((d) => parseNeed(d).key === PERMIT_KEY)) follow(PERMIT_KEY, n.key);
  }
  if (binned) left.push(`${binned} ${binned === 1 ? 'wait' : 'waits'} on steps in the bin, dropped with them.`);

  const nodes: ProcessNodeDefT[] = ordered.map((n) => {
    const o = was.get(n.key);
    // Someone outside the job (an inspector) keeps the old tree's inspection, which the job passes or fails.
    const kind: NodeKind = n.kind === 'task' && n.who === 'outside' && o?.kind === 'inspection' ? 'inspection' : KIND[n.kind];
    const owner: TaskOwner | undefined = n.who ? OWNER_OF_WHO[n.who] : o?.owner && (TASK_OWNERS as readonly string[]).includes(o.owner) ? o.owner : undefined;
    const trade = kind === 'work' ? (n.trade ?? undefined) : undefined;
    // The ends sit in no phase on the board; in a job the start is at the top of the first and the end at the foot of the last.
    const ends: PhaseKey | undefined = n.kind === 'start' ? PHASE_KEYS[0] : n.kind === 'end' ? PHASE_KEYS.at(-1) : undefined;
    const phase = PHASE_KEYS.includes(n.phase as PhaseKey) ? (n.phase as PhaseKey) : (ends ?? o?.phase);
    const why = n.note?.trim() || o?.why;
    const source = (NODE_SOURCES as readonly string[]).includes(n.source ?? '') ? (n.source as ProcessNodeDefT['source']) : o?.source;
    const days = daysOf(n) ?? (kind === 'gate' || kind === 'milestone' ? undefined : o?.days);
    const before = [...(after.get(n.key) ?? [])];
    // The special tasks it may carry (spec "Pattern Mapping" §3): its kind's, at the top alone; a task made an inspection
    // here, or one in a step's list, carries none, and what is left off is said, never lost quietly.
    const special = specialsOf(n).filter((k) => SPECIAL_TASKS[k].kinds.includes(kind) && n.parent === null);
    for (const k of specialsOf(n)) if (!special.includes(k)) left.push(`${SPECIAL_TASKS[k].name} was left off ${titleCase(n.title)}: ${n.parent !== null ? 'a task in a step’s list carries no special task' : `it is ${KIND_WORDS_OF[kind]}`}.`);
    const node: ProcessNodeDefT = {
      key: n.key,
      title: n.title,
      kind,
      ...(n.parent !== null ? { parent: n.parent, basic: ['client', 'internal'] as ProcessNodeDefT['basic'] } : {}),
      ...(before.length ? { before } : {}),
      ...(phase ? { phase } : {}),
      // A gate's owner is who approves it (gates A): none, and it happens, NSB marking it done.
      ...(owner && kind !== 'milestone' && (kind !== 'gate' || n.who !== null) ? { owner } : {}),
      ...(trade ? { trade } : {}),
      ...(days !== undefined ? { days } : {}),
      ...(why ? { why } : {}),
      ...(n.clientNote?.trim() ? { clientNote: n.clientNote.trim() } : {}),
      ...(o?.notes ? { notes: o.notes } : {}),
      ...(o?.clientLine ? { clientLine: o.clientLine } : {}),
      ...(o?.repeatable && n.parent === null && kind === 'work' ? { repeatable: true } : {}),
      ...(o?.flagged ? { flagged: true } : {}),
      ...(o?.trades?.length ? { trades: o.trades } : {}),
      ...(source ? { source } : {}),
      ...(n.permit ? { permit: true } : {}),
      ...(n.inspection ? { inspection: true } : {}),
      ...(n.preInstall ? { preInstall: true } : {}),
      ...(n.deliversReview && n.kind === 'task' ? { deliversReview: true } : {}),
      // An estimate is delivered by work or a gate (the Quote the client approves), never a milestone or a decision.
      ...(n.deliversEstimate && (n.kind === 'task' || n.kind === 'gate') ? { deliversEstimate: true } : {}),
      ...(special.length ? { special } : {}),
      // A decision's options, their ids and words, and whether one is chosen or any (design study 22): on a job, deciding
      // it records the choice. A line drawn from an option is a line from the decision (`before`, above).
      ...(n.kind === 'decision' && n.options.length ? { options: n.options.map((o) => ({ id: o.id, text: o.text })), choose: chooseOf(n) } : {}),
    };
    return node;
  });

  // The holds in a step's own list: NSB's check before it goes in, first; the inspector's, last.
  const taken = new Set(nodes.map((n) => n.key));
  const fresh = (base: string) => {
    let k = base;
    for (let i = 2; taken.has(k); i++) k = `${base}-${i}`;
    taken.add(k);
    return k;
  };
  const extra = new Map<string, { first: ProcessNodeDefT[]; last: ProcessNodeDefT[] }>();
  for (const n of nodes) {
    if (n.parent !== undefined || n.kind !== 'work' || !(n.inspection || n.preInstall)) continue;
    const tasks = nodes.filter((c) => c.parent === n.key);
    const task = (key: string, title: string, kind: NodeKind, owner: TaskOwner): ProcessNodeDefT => ({
      key,
      title,
      kind,
      parent: n.key,
      basic: ['client', 'internal'],
      ...(n.phase ? { phase: n.phase } : {}),
      owner,
      ...(n.source ? { source: n.source } : {}),
    });
    const first: ProcessNodeDefT[] = [];
    const last: ProcessNodeDefT[] = [];
    const inspected = n.inspection && !tasks.some((c) => c.kind === 'inspection');
    // The work itself, for the holds to sit either side of, where the step lists nothing of its own.
    const work = tasks.length === 0 && (inspected || n.preInstall) ? task(fresh(`${n.key}.work`), 'The work', 'work', n.owner ?? 'nsb') : null;
    // The work the holds sit either side of: never an inspection, which comes after it (so locking in again holds the same).
    const doing = [...tasks.filter((c) => c.kind !== 'inspection'), ...(work ? [work] : [])];
    if (n.preInstall) {
      const check = task(fresh(`${n.key}.pre-install`), 'Site checked ready', 'inspection', 'nsb');
      check.before = doing.map((c) => c.key);
      first.push(check);
    }
    if (work) last.push(work);
    if (inspected) {
      const inspection = task(fresh(`${n.key}.inspection`), 'Inspection', 'inspection', 'authority');
      for (const c of doing) c.before = [...new Set([...(c.before ?? []), inspection.key])];
      last.push(inspection);
    }
    if (first.length || last.length) extra.set(n.key, { first, last });
  }
  if (extra.size) {
    const top = (n: ProcessNodeDefT): string => (n.parent === undefined ? n.key : top(nodes.find((x) => x.key === n.parent)!));
    const placed: ProcessNodeDefT[] = [];
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i]!;
      placed.push(n);
      const mine = extra.get(n.key);
      if (mine) placed.push(...mine.first);
      // After the last of a step's own list, what goes last in it.
      const t = top(n);
      const next = nodes[i + 1];
      if (extra.has(t) && (!next || top(next) !== t)) placed.push(...extra.get(t)!.last);
    }
    nodes.splice(0, nodes.length, ...placed);
  }

  // One step of the tree carries each special task (Pattern mapping is the job's one language): a tree with two is
  // refused, in words, never one of them dropped quietly.
  for (const k of SPECIAL_KEYS) {
    const carrying = nodes.filter((n) => n.special?.includes(k));
    if (carrying.length > 1) {
      const names = carrying.map((n) => `“${titleCase(n.title)}”`);
      throw new Error(`${SPECIAL_TASKS[k].name} is on ${names.slice(0, -1).join(', ')} and ${names.at(-1)!}: one step of the tree carries it, so take it off all but one`);
    }
  }

  const known = new Set(nodes.map((n) => n.key));

  // The phases as the process tree page set them: what closes each, where the tree has it, and how sure the price is once it is.
  const isPhase = (k: string): k is PhaseKey => (PHASE_KEYS as readonly string[]).includes(k);
  const phases: TreePhase[] = [];
  for (const p of t.phases) {
    if (!isPhase(p.key)) {
      left.push(`${p.name} is not one of the five phases, so it is left out; its steps sit where the old tree had them.`);
      continue;
    }
    const closedBy = p.closedBy !== null && known.has(p.closedBy) ? p.closedBy : null;
    if (p.closedBy !== null && closedBy === null) left.push(`${p.name} was closed by ${titleCase(byKey.get(p.closedBy)?.title ?? p.closedBy)}, which is not in the tree, so on a job it ends with its last step.`);
    phases.push({ key: p.key, name: p.name, what: p.what, closedBy, sureAfter: p.sureAfter, ...(p.share !== undefined ? { share: p.share } : {}) });
  }
  phases.sort((a, b) => PHASE_KEYS.indexOf(a.key) - PHASE_KEYS.indexOf(b.key));
  // A board with no phases of the five keeps the tree before's, what closes each where the tree still has it.
  const treePhases = phases.length ? phases : old.phases.map((p) => ({ ...p, closedBy: p.closedBy !== null && known.has(p.closedBy) ? p.closedBy : null }));
  const refused = unopened(treePhases, nodes);
  if (refused) throw new Error(refused);

  const doc: ProcessTreeDoc = {
    key: old.key,
    name: old.name,
    source: 'The process tree, locked in',
    ...(known.has(BUILDER_START) ? { start: BUILDER_START } : {}),
    byHand: true,
    lockedFrom: from,
    phases: treePhases,
    nodes,
  };
  return { doc: ProcessTreeDocSchema.parse(doc), left };
}

/**
 * One gate opens each phase after the first: the gate that closes the phase before it (Jason, 2026-10-07, phase
 * transitions: "One gate opens each transition"). The first phase that has none, in words; null when every one has.
 * A closer the tree does not have (lock-in left it out) is none.
 */
function unopened(phases: readonly TreePhase[], nodes: readonly ProcessNodeDefT[]): string | null {
  const ordered = [...phases].sort((a, b) => PHASE_KEYS.indexOf(a.key) - PHASE_KEYS.indexOf(b.key));
  for (let i = 1; i < ordered.length; i++) {
    const before = ordered[i - 1]!;
    const closer = before.closedBy !== null ? nodes.find((n) => n.key === before.closedBy) : undefined;
    if (closer?.kind === 'gate') continue;
    const phase = ordered[i]!;
    return closer
      ? `${phase.name} has no gate to open it: ${before.name}, the phase before it, is closed by “${titleCase(closer.title)}”, which is not a gate`
      : `${phase.name} has no gate to open it: ${before.name}, the phase before it, has no gate closing it`;
  }
  return null;
}
